import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useEducationAuth } from '../contexts/EducationAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 90 * 1000; // 90 seconds — light enough to be passive
const ONBOARDING_KEY_PREFIX = 'shs_admin_pulse_onboarded_';
const ONBOARDING_DELAY_MS = 1500;

const STATE_META = {
  healthy: {
    dot: 'bg-emerald-400',
    pulse: 'bg-emerald-400/40',
    label: 'All systems normal',
    short: 'OK',
    text: 'text-emerald-300',
  },
  warning: {
    dot: 'bg-amber-400',
    pulse: 'bg-amber-400/40',
    label: 'Warning detected',
    short: 'Warning',
    text: 'text-amber-300',
  },
  critical: {
    dot: 'bg-rose-500',
    pulse: 'bg-rose-500/50',
    label: 'Critical issue detected',
    short: 'Critical',
    text: 'text-rose-300',
  },
  unknown: {
    dot: 'bg-slate-500',
    pulse: 'bg-slate-500/40',
    label: 'Health unknown',
    short: '—',
    text: 'text-slate-400',
  },
};

const deriveState = (summary) => {
  if (!summary) return 'unknown';
  // Prefer explicit tier-aware backend classification when available.
  if (summary.overall_health === 'critical') return 'critical';
  if (summary.overall_health === 'warning') return 'warning';
  if (summary.overall_health === 'healthy') return 'healthy';
  // Fallback for older payloads without overall_health.
  if ((summary.critical_severity_count || 0) > 0) return 'critical';
  if ((summary.warning_severity_count || 0) > 0) return 'warning';
  return 'healthy';
};

/**
 * Lightweight admin-only health pulse.
 * - Renders only when authenticated user has `shs_admin` role
 * - Polls `/api/admin/diagnostics/db-health` every 90s in the background
 * - Displays a 3-state indicator that links to /admin/db-health
 * - Zero impact on non-admin users (returns null)
 */
const AdminFooterHealthPulse = () => {
  const { user, token, isAdmin } = useEducationAuth();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const intervalRef = useRef(null);

  // One-time onboarding tooltip per admin account per device
  useEffect(() => {
    if (!isAdmin || !user?.id) return undefined;
    const storageKey = `${ONBOARDING_KEY_PREFIX}${user.id}`;
    let dismissed = false;
    try { dismissed = localStorage.getItem(storageKey) === '1'; } catch { /* private mode */ }
    if (dismissed) return undefined;
    const t = window.setTimeout(() => setShowOnboarding(true), ONBOARDING_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [isAdmin, user?.id]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    if (user?.id) {
      try { localStorage.setItem(`${ONBOARDING_KEY_PREFIX}${user.id}`, '1'); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    // Only run for admins
    if (!isAdmin || !token) return undefined;

    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/diagnostics/db-health`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (cancelled) return;
        setSummary(data.summary || null);
        setError(false);
      } catch {
        if (!cancelled) setError(true);
      }
    };

    fetchHealth();
    intervalRef.current = window.setInterval(fetchHealth, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [isAdmin, token]);

  if (!user || !isAdmin) return null;

  const state = error ? 'unknown' : deriveState(summary);
  const meta = STATE_META[state];

  const detail = summary
    ? `${summary.healthy}/${summary.total_tracked} healthy${summary.missing ? ` · ${summary.missing} missing` : ''}${summary.empty_warning ? ` · ${summary.empty_warning} empty` : ''}`
    : 'Loading…';

  return (
    <div className="relative inline-flex">
      {showOnboarding && (
        <div
          className="absolute bottom-full mb-2 right-0 md:right-auto md:left-1/2 md:-translate-x-1/2 z-30 w-64 rounded-lg bg-slate-900 border border-amber-400/40 shadow-2xl shadow-black/40 px-3 py-2.5 animate-in fade-in slide-in-from-bottom-1 duration-300"
          role="dialog"
          aria-live="polite"
          data-testid="admin-pulse-onboarding"
        >
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex-shrink-0">
              <span className="block h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-[12px] text-slate-100 leading-snug">
                System health monitoring is available here.
              </p>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissOnboarding(); }}
                className="mt-1.5 text-[11px] font-semibold text-amber-300 hover:text-amber-200"
                data-testid="admin-pulse-onboarding-dismiss"
              >
                {document.documentElement.lang === 'es' ? 'Entendido' : 'Got it'}
              </button>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissOnboarding(); }}
              className="text-slate-500 hover:text-slate-300 -mt-0.5 -mr-0.5"
              aria-label={document.documentElement.lang === 'es' ? 'Descartar consejo de introducción' : 'Dismiss onboarding tip'}
              data-testid="admin-pulse-onboarding-close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Tooltip arrow pointing down to the pulse */}
          <div className="absolute -bottom-1.5 right-4 md:right-auto md:left-1/2 md:-translate-x-1/2 h-3 w-3 bg-slate-900 border-r border-b border-amber-400/40 rotate-45" />
        </div>
      )}

      <Link
        to="/admin/db-health"
        className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors text-[11px] text-slate-300"
        title={`${meta.label} — click for details`}
        data-testid="admin-footer-health-pulse"
      >
        <span className="relative flex items-center justify-center" aria-hidden="true">
          <span className={`absolute h-3 w-3 rounded-full ${meta.pulse} animate-ping opacity-75`} />
          <span className={`relative h-2 w-2 rounded-full ${meta.dot}`} />
        </span>
        <span className={`font-medium ${meta.text}`}>{meta.short}</span>
        <span className="hidden md:inline text-slate-500">·</span>
        <span className="hidden md:inline text-slate-500 truncate max-w-[14rem]">{detail}</span>
        <span className="sr-only">{meta.label}. {detail}</span>
      </Link>
    </div>
  );
};

export default AdminFooterHealthPulse;
